import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Clock, User, Phone, Mail, AlertCircle, Loader2, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import axios from 'axios';
import { API } from '@/config/api';
import { useCart } from '../context/CartContext';
import { orderPath } from '../lib/orderPath';
import { payAgain } from '@/components/checkout';
import { toast } from 'sonner';

const NOT_PAID_TEXT = {
  refunded: ['This order was refunded', 'The payment was refunded. Contact the restaurant if you have questions.'],
  partially_refunded: ['Part of this order was refunded', 'Contact the restaurant if you have questions.'],
  voided: ['This payment was voided', 'Contact the restaurant if you have questions.'],
};

export default function ConfirmationPage() {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const { restaurantSlug } = useCart();
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('t') || '';
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState(null);
  const [paying, setPaying] = useState(false);


  const fetchOrder = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/orders/public/${orderNumber}`, { params: { t: accessToken } });
      setOrder(response.data);
    } catch (err) {
      console.error('Failed to load order:', err);
      setError(
        err.response?.status === 404
          ? 'Order not found. Please check your order number.'
          : 'Unable to load order details. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [orderNumber, accessToken]);

  useEffect(() => {
    if (orderNumber) {
      fetchOrder();
    }
  }, [orderNumber, fetchOrder]);

  const handleOrderAgain = () => {
    navigate(orderPath(restaurantSlug ? `storefront/${restaurantSlug}` : ''));
  };

  // Not paid: the open page (or a new one); paid late -> the server says so and this page reloads it.
  const handlePayNow = async () => {
    setPaying(true);
    try {
      const res = await payAgain(orderNumber, accessToken);
      if (res.payment_status === 'paid') { await fetchOrder(); return; }
      if (res.payment?.pay_url) { window.location.assign(res.payment.pay_url); return; }
      navigate(orderPath(`checkout?order=${orderNumber}&t=${accessToken}`));
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not open the payment page. Please try again.');
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Loading your order...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <Alert className="border-red-200 bg-red-50">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <AlertDescription className="text-red-800">
              {error}
            </AlertDescription>
          </Alert>
          <div className="mt-6 space-y-3">
            <Button 
              onClick={fetchOrder}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              Try Again
            </Button>
            <Button 
              onClick={handleOrderAgain}
              variant="outline"
              className="w-full"
            >
              Back to Menu
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  // Only a PAID order is confirmed (the kitchen gets it once it is paid).
  if (order.payment_status !== 'paid') {
    const [title, text] = NOT_PAID_TEXT[order.payment_status] || (order.status === 'cancelled'
      ? ['This order was cancelled', 'It was not sent to the kitchen. If you see a charge, the restaurant will confirm or refund it.']
      : ['Payment not completed', 'Your order is saved but has not been paid yet, so the kitchen has not received it.']);
    const canPay = !NOT_PAID_TEXT[order.payment_status] && order.status !== 'cancelled';
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-testid="confirmation-not-paid">
        <Card className="max-w-md w-full shadow-xl">
          <CardContent className="p-8 text-center space-y-5">
            <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
              <AlertCircle className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900" data-testid="confirmation-title">{title}</h1>
              <p className="text-gray-600 mt-2">{text}</p>
              <p className="text-sm text-gray-500 mt-2">Order #{orderNumber}</p>
            </div>
            {canPay && (
              <Button onClick={handlePayNow} disabled={paying} className="w-full h-12 text-base font-semibold bg-blue-600 hover:bg-blue-700"
                data-testid="confirmation-pay-now">
                {paying ? <Loader2 className="h-5 w-5 animate-spin" /> : <><CreditCard className="h-4 w-4 mr-2" />Pay now</>}
              </Button>
            )}
            <Button onClick={handleOrderAgain} variant="outline" className="w-full h-11">Back to Menu</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Success Header */}
      <div className="bg-gradient-to-br from-green-500 to-green-600 text-white py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="mb-4 flex justify-center">
            <div className="bg-white rounded-full p-3">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2" data-testid="confirmation-title">
            Order Confirmed!
          </h1>
          <p className="text-green-50 text-lg mb-4">
            Your order has been received and is being prepared
          </p>
          <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-4 inline-block">
            <p className="text-sm text-green-50 mb-1">Order Number</p>
            <p className="text-4xl font-bold">#{orderNumber}</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 -mt-4">
        {/* Pickup Information */}
        <Card className="mb-4 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="bg-blue-100 rounded-full p-3">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">
                  Pickup Information
                </h2>
                <p className="text-gray-600 mb-2">
                  Estimated pickup time
                </p>
                <p className="text-2xl font-bold text-blue-600">
                  {order.estimated_pickup_time}
                </p>
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-medium text-gray-700">
                    Order Type: <span className="text-gray-900">Pickup</span>
                  </p>
                  {order.status && (
                    <p className="text-sm text-gray-600 mt-1">
                      Status: <span className="font-medium text-gray-900 capitalize">{order.status}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Customer Information */}
        <Card className="mb-4 shadow-lg">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Contact Information
            </h2>
            <div className="space-y-3">
              {order.customer_name && (
                <div className="flex items-center gap-3">
                  <User className="h-5 w-5 text-gray-400" />
                  <span className="text-gray-700">{order.customer_name}</span>
                </div>
              )}
              {order.customer_phone && (
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-gray-400" />
                  <span className="text-gray-700">{order.customer_phone}</span>
                </div>
              )}
              {order.customer_email && (
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <span className="text-gray-700">{order.customer_email}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Order Items */}
        <Card className="mb-4 shadow-lg">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Order Summary
            </h2>
            
            <div className="space-y-4">
              {order.items && order.items.map((item, index) => (
                <div key={index} className="pb-4 border-b last:border-b-0 last:pb-0">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {item.quantity}x {item.name}
                      </p>
                      
                      {/* Modifiers */}
                      {item.modifiers && item.modifiers.length > 0 && (
                        <div className="mt-1 text-sm text-gray-600">
                          {item.modifiers.map((group, idx) => (
                            <div key={idx}>
                              {group.options && group.options.map((opt, optIdx) => (
                                <span key={optIdx} className="block">
                                  • {opt.name} {opt.price > 0 && `(+$${opt.price.toFixed(2)})`}
                                </span>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Special Instructions */}
                      {item.special_instructions && (
                        <p className="text-sm text-gray-500 italic mt-1">
                          Note: {item.special_instructions}
                        </p>
                      )}
                    </div>
                    <p className="font-medium text-gray-900 ml-4">
                      ${item.total_price?.toFixed(2) || (item.unit_price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Special Instructions for entire order */}
            {order.special_instructions && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm font-medium text-gray-700 mb-1">
                  Special Instructions:
                </p>
                <p className="text-sm text-gray-600">{order.special_instructions}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pricing Summary */}
        <Card className="mb-4 shadow-lg">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Payment Summary
            </h2>
            
            <div className="space-y-2">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span className="font-medium">${order.subtotal?.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between text-gray-600">
                <span>Tax</span>
                <span className="font-medium">${order.tax?.toFixed(2)}</span>
              </div>
              
              {order.tip > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>Tip</span>
                  <span className="font-medium">${order.tip?.toFixed(2)}</span>
                </div>
              )}
              
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="text-lg font-bold text-gray-900">Total Paid</span>
                  <span className="text-2xl font-bold text-green-600">
                    ${(order.customer_paid_total || order.total)?.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Payment Successful</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Your payment has been processed successfully
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="space-y-3 mb-8">
          <Button
            onClick={() => navigate(orderPath(`status/${orderNumber}?t=${accessToken}`))}
            className="w-full h-12 text-lg font-semibold bg-green-600 hover:bg-green-700"
          >
            Track Order Status
          </Button>
          <Button
            onClick={handleOrderAgain}
            variant="outline"
            className="w-full h-12 text-lg"
          >
            Order Again
          </Button>
          <Button
            onClick={handleOrderAgain}
            variant="outline"
            className="w-full h-12 text-lg"
          >
            Back to Menu
          </Button>
        </div>

        {/* Additional Info */}
        <div className="text-center text-sm text-gray-500 mb-4">
          <p>Thank you for your order!</p>
          <p className="mt-1">We'll have your food ready soon.</p>
        </div>
      </div>
    </div>
  );
}
